import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { Cluster } from "#modules/quoteRequestLines/Cluster";
import { ConsumableData } from "#modules/quoteRequestLines/ConsumableData";
import { useQuoteRequestLineContent } from "#modules/quoteRequestLines/domain/useQuoteRequestLineContent";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLineActionsMenu } from "#modules/quoteRequestLines/QuoteRequestLineActionsMenu";
import { PartDimensionsModal } from "#modules/quoteRequestLines/view/PartDimensionsModal";
import { PredictionData } from "#modules/quoteRequestLines/views/PredictionData/PredictionData";
import { QuoteRequestLineBatchRightSection } from "#modules/quoteRequestLines/views/QuoteRequestLineRightSection/QuoteRequestLineBatchRightSection";
import { QuoteRequestLineRightSection } from "#modules/quoteRequestLines/views/QuoteRequestLineRightSection/QuoteRequestLineRightSection";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Flex } from "#shared/view/components/Flex";
import { Group } from "#shared/view/components/Group";
import { AssemblyWithChildren, getQuoteRequestLineProductLines, JobStatusType } from "shared";

import { PartHeader } from "./PartHeader";
import { UnitEstimatedPriceDisplay } from "./UnitEstimatedPriceDisplay";

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  part: PartDto | undefined;
  openDetailsPanelForQuoteRequestLine: (quoteRequestLineId: string) => void;
  toggleAssembly: () => void;
  isAssemblyOpen: boolean;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
  isDetailsPanelDisplayable: boolean;
  hasChildren: boolean;
  isChild: boolean;
  polledPredictionStatus?: JobStatusType | null;
};

export const QuoteRequestLineContent = ({
  quoteRequestLine,
  part,
  openDetailsPanelForQuoteRequestLine,
  toggleAssembly,
  isAssemblyOpen,
  assembliesInBatch,
  isDetailsPanelDisplayable,
  hasChildren,
  isChild,
  polledPredictionStatus,
}: Props) => {
  const {
    isConsumable,
    shouldDisplayClusteringZone,
    isPartBeingAnalyzed,
    updateMaterial,
    updateQuantity,
    isPredicting,
    predictProcessSteps,
    isPredictionError,
    editCluster,
    updateProductLine,
    quantityInAssembly,
    overlayOpened,
    closeOverlay,
    overlayDefaultTab,
    openThumbnailOnAnnotated,
    dimensionChipOpened,
    closeDimensionChip,
  } = useQuoteRequestLineContent({
    quoteRequestLine,
    part,
    hasChildren,
    isChild,
    polledPredictionStatus,
  });

  const isBatch = assembliesInBatch.length > 1;

  return (
    <>
      {part && (
        <PartDimensionsModal
          part={part}
          quoteRequestLine={quoteRequestLine}
          partDimensionsModalOpened={dimensionChipOpened}
          closePartDimensionsModal={closeDimensionChip}
        />
      )}
      <Flex
        justify={"space-between"}
        w={"100%"}
        gap="xs"
        wrap="wrap"
        data-testid={`quote-request-line-content-${quoteRequestLine.part.name}`}
      >
        <Group wrap={"nowrap"}>
          <Flex gap="xs" align={"center"} miw={350}>
            {hasChildren && (
              <ActionIcon
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAssembly();
                }}
                size={20}
                aria-label={
                  isAssemblyOpen
                    ? `Collapse assembly ${quoteRequestLine.part.name}`
                    : `Expand assembly ${quoteRequestLine.part.name}`
                }
                aria-expanded={isAssemblyOpen}
              >
                <Icon name={isAssemblyOpen ? "minus" : "plus"} />
              </ActionIcon>
            )}

            <PartHeader
              quoteRequestLine={quoteRequestLine}
              hasChildren={hasChildren}
              isConsumable={isConsumable}
              fontSize={16}
              thumbnailSize={{ width: 55, height: 55 }}
              part={part}
              showMaterial={false}
              overlayOpened={overlayOpened}
              onOverlayClose={closeOverlay}
              overlayDefaultTab={overlayDefaultTab}
            />
          </Flex>

          <Group mx="md" gap="xs" onClick={(e) => e.stopPropagation()}>
            {!isPartBeingAnalyzed && !isConsumable && (
              <PredictionData
                onQuantityChange={updateQuantity}
                onMaterialChange={updateMaterial}
                isPredicting={isPredicting}
                assembliesInBatch={assembliesInBatch}
                quoteRequestLine={quoteRequestLine}
                isChild={isChild}
              />
            )}

            {shouldDisplayClusteringZone && (
              <Cluster
                quoteRequestLine={quoteRequestLine}
                onClick={editCluster}
                assembliesInBatch={assembliesInBatch}
              />
            )}

            {isChild && !isConsumable && quantityInAssembly > 1 && (
              <UnitEstimatedPriceDisplay quoteRequestLine={quoteRequestLine} />
            )}

            {isConsumable && (
              <ConsumableData
                onConsumableDataChange={updateProductLine}
                productLines={getQuoteRequestLineProductLines(quoteRequestLine)}
              />
            )}
          </Group>
        </Group>

        <Flex align={"center"} justify={"flex-end"} miw={200} onClick={(e) => e.stopPropagation()}>
          {isBatch ? (
            <QuoteRequestLineBatchRightSection
              assembliesInBatch={assembliesInBatch}
              partType={quoteRequestLine.part.type}
              openDetailsPanelForQuoteRequestLine={openDetailsPanelForQuoteRequestLine}
              isPredicting={isPredicting}
              hasChildren={hasChildren}
            />
          ) : (
            part && (
              <QuoteRequestLineRightSection
                predictProcessSteps={predictProcessSteps}
                isPredictionError={isPredictionError}
                quoteRequestLine={quoteRequestLine}
                openDetailsPanelForQuoteRequestLine={openDetailsPanelForQuoteRequestLine}
                part={part}
                onOpenThumbnailOverlay={openThumbnailOnAnnotated}
                assembliesInBatch={assembliesInBatch}
                hasChildren={hasChildren}
                isChild={isChild}
              />
            )
          )}

          <QuoteRequestLineActionsMenu
            part={part}
            openDetailsPanelForQuoteRequestLine={openDetailsPanelForQuoteRequestLine}
            assembliesInBatch={assembliesInBatch}
            quoteRequestLine={quoteRequestLine}
            isDetailsPanelDisplayable={isDetailsPanelDisplayable}
          />
        </Flex>
      </Flex>
    </>
  );
};
